import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Router, RouterLink } from "@angular/router";
import { Location } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { ConfirmDialogComponent } from "src/app/shared/dialogs/confirm-dialog/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PlatformService } from "src/app/core/services/platform.service";
import { firstValueFrom } from "rxjs";
import { UserService } from "@kortobaa-front/authentication";
import { FirebaseService } from "src/app/core/services/firebase.service";
import { CountriesService } from "src/app/shared/services/countries.service";
import { TenantContextService } from "src/app/core/services/tenant-context.service";

const INSTITUTE_ONLY_ITEMS = new Set([
  "teacher-pipeline",
  "student-pipeline",
  "announcements",
  "gamification",
  "curriculum-tree",
]);

const SUPER_ADMIN_ONLY_ITEMS = new Set(["tenant-pipeline", "super-admin-section"]);

// 2026-05-06 G+6 — tenant_admin-only items (governance / config).
// tenant_manager + supervisor are blocked at the route level by
// TenantAdminGuard; we also hide the menu links so they don't see
// dead entries. super_admin always sees everything for impersonation.
//
// 2026-05-07 — reverted: 'modification' + 'money-transfer-requests'
// are operations-tier (tenant_manager handles approvals as part of
// day-to-day ops).
const TENANT_ADMIN_ONLY_ITEMS = new Set(["feature-flags", "pages", "update-version", "mobile-page-config", "institute-settings"]);

// 2026-05-07 — Slate A backend endpoints shipped (analytics +
// /super/audit-log + /super/feature-flags/bulk + POST /super/institutes
// + /super/impersonate). All previously gap-hidden items are back.
// Keep the set in place so future gaps can be added with one line.
const BACKEND_GAP_ITEMS = new Set<string>([]);

// Same set, but matched against child `route` segments (children
// don't carry an `id` field — they're {title, route} pairs). Used
// inside `visibleMenu` to filter sub-menu links of a parent that
// survives the top-level gap filter (e.g., the super-admin-section
// children: super/audit-log, super/feature-flags, super/onboarding
// stay hidden until backend ships).
const BACKEND_GAP_ROUTE_SEGMENTS: string[] = [];

// 2026-05-06 — minimal type shape for menu entries. Replaces 4 `any`
// usages across the side-menu without locking down the full shape
// (icon emojis, conditional fields stay loose).
export interface MenuChild {
  title: string;
  route: string;
  icon?: string;
  hasChildren?: boolean;
}

// 2026-05-07 — IA grouping. Each top-level item now declares which
// section it belongs to so the sidebar renders subtle group dividers
// (Operations / Governance / Platform). Defaults to 'operations'
// when omitted so legacy items don't disappear.
export type MenuGroup = "operations" | "governance" | "platform";

export interface MenuItem extends MenuChild {
  id: string;
  hasChildren: boolean;
  children?: MenuChild[];
  group?: MenuGroup;
}

export interface MenuSection {
  group: MenuGroup;
  titleKey: string;
  items: MenuItem[];
}

const MENU_GROUP_ORDER: { group: MenuGroup; titleKey: string }[] = [
  { group: "operations", titleKey: "sideMenu.groups.operations" },
  { group: "governance", titleKey: "sideMenu.groups.governance" },
  { group: "platform", titleKey: "sideMenu.groups.platform" },
];
interface AppConfig {
  isFreePlatform?: boolean;
  deploymentMode?: string;
}
interface ConfigurationResponse {
  data?: AppConfig & { deploymentMode?: string };
}

@Component({
  selector: "app-side-menu",
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, MatIconModule],
  templateUrl: "./side-menu.component.html",
  styleUrls: ["./side-menu.component.scss"],
})
export class SideMenuComponent {
  // Emits open/close state when a sub-menu is toggled.
  @Output() toggleSubMenu: EventEmitter<boolean> = new EventEmitter();
  @Input() sideNavToggleBtn!: boolean;
  @Input() subMenu!: boolean;
  openFlag: boolean = false;
  routeTitle: string = "";
  /** Current url path (e.g. `/students`). Set from Router events in
   *  the constructor so isItemActive / isChildActive can compare. */
  route: string = "";
  sideMenu = [
    {
      id: "home",
      title: "home",
      route: "/home",
      icon: "home",
      hasChildren: false,
      children: [] as any,
      group: "operations" as MenuGroup,
    },
    {
      id: "students",
      title: "students",
      route: "/students",
      icon: "admin_panel_settings",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
    {
      id: "tenant-pipeline",
      title: "tenant-pipeline",
      route: "/tenant-pipeline",
      icon: "domain_add",
      hasChildren: false,
      group: "platform" as MenuGroup,
    },
    {
      id: "teacher-pipeline",
      title: "teacher-pipeline",
      route: "/teacher-pipeline",
      icon: "how_to_reg",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
    {
      id: "student-pipeline",
      title: "student-pipeline",
      route: "/student-pipeline",
      icon: "person_add",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
    {
      id: "curriculum-tree",
      title: "curriculum-tree",
      route: "/curriculum-tree",
      icon: "account_tree",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
    {
      id: "announcements",
      title: "announcements",
      route: "/announcements",
      icon: "campaign",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
    {
      // H1 — Maqraahs management (list + edit + soft-delete). The
      // create-maqraah page is reachable from the list's "+ create"
      // button so it gets no separate menu entry.
      id: "manage-maqraahs",
      title: "manage-maqraahs",
      route: "/maqraah",
      icon: "groups_2",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
    {
      id: "gamification",
      title: "gamification",
      route: "/gamification",
      icon: "emoji_events",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
    {
      title: "requests-join",
      route: "/requests-join/active-apps",
      icon: "group_add",
      hasChildren: true,
      id: "requests",
      group: "operations" as MenuGroup,
      children: [
        {
          title: "pending-apps",
          route: "/requests-join/pending-apps",
        },
        {
          title: "active-apps",
          route: "/requests-join/active-apps",
        },
        {
          title: "declined-apps",
          route: "/requests-join/declined-apps",
        },
      ],
    },
    {
      title: "modification-requests",
      route: "/modification-requests/available-requests",
      icon: "edit_square",
      hasChildren: true,
      id: "modification",
      group: "operations" as MenuGroup,
      children: [
        {
          title: "available-requests",
          route: "/modification-requests/available-requests",
        },
        {
          title: "rejected-requests",
          route: "/modification-requests/rejected-requests",
        },
      ],
    },
    {
      id: "teachers",
      title: "teachers",
      route: "/teachers/all",
      icon: "verified",
      hasChildren: true,
      group: "operations" as MenuGroup,
      children: [
        {
          title: "all",
          route: "/teachers/all",
        },
        {
          title: "active-teachers",
          route: "/teachers/active-teachers",
        },
        {
          title: "inactive-teachers",
          route: "/teachers/inactive-teachers",
        },
      ],
    },
    {
      id: "appointments",
      title: "appointments",
      route: "/appointments",
      icon: "calendar_clock",
      hasChildren: true,
      group: "operations" as MenuGroup,
      children: [
        // {
        //   title: "appointments",
        //   route: "/appointments/all",
        // },
        {
          title: "initiated",
          route: "/appointments/initiated",
        },
        {
          title: "started",
          route: "/appointments/started",
        },
        {
          title: "ended",
          route: "/appointments/ended",
        },
        {
          title: "canceled_by_student",
          route: "/appointments/canceled_by_student",
        },
        {
          title: "canceled_by_teacher",
          route: "/appointments/canceled_by_teacher",
        },
      ],
    },
    {
      id: "courses-list",
      title: "courses-list",
      route: "/courses-list",
      icon: "event_note",
      hasChildren: true,
      group: "operations" as MenuGroup,
      children: [
        {
          title: "in_review",
          route: "/courses-list/in_review",
        },
        {
          title: "enabled",
          route: "/courses-list/enabled",
        },
        {
          title: "paused",
          route: "/courses-list/paused",
        },
        {
          title: "disabled",
          route: "/courses-list/disabled",
        },
        {
          title: "rejected",
          route: "/courses-list/rejected",
        },
        {
          title: "closed_by_user",
          route: "/courses-list/closed_by_user",
        },
        {
          title: "courses-expiration",
          route: "/courses-expiration",
        },
      ],
    },
    {
      title: "meetings",
      route: "/meetings",
      icon: "groups",
      hasChildren: true,
      id: "meetings",
      group: "operations" as MenuGroup,
      children: [
        {
          title: "active-meetings",
          route: "/meetings/active-meetings",
        },
        {
          title: "scheduled-meetings",
          route: "/meetings/scheduled-meetings",
        },
        {
          title: "ended-meetings",
          route: "/meetings/ended-meetings",
        },
      ],
    },
    {
      id: "payments",
      title: "payments",
      route: "/payments",
      icon: "payments",
      hasChildren: false,
      condition: true,
      group: "operations" as MenuGroup,
    },
    {
      id: "money-transfer-requests",
      title: "money-transfer-requests",
      route: "/money-transfer-requests",
      icon: "request_page",
      hasChildren: false,
      condition: true,
      group: "operations" as MenuGroup,
    },
    {
      id: "notifications",
      title: "notifications",
      route: "/notifications/teachers-notifications",
      icon: "notifications",
      hasChildren: true,
      group: "operations" as MenuGroup,
      children: [
        {
          title: "teacher-notifications",
          route: "/notifications/teacher-notifications",
        },
        {
          title: "student-notifications",
          route: "/notifications/student-notifications",
        },
      ],
    },
    {
      id: "reports",
      title: "reports",
      route: "/reports/error-reports",
      icon: "report",
      hasChildren: true,
      group: "operations" as MenuGroup,
      children: [
        {
          title: "error-reports",
          route: "/reports/error-reports",
        },
        {
          title: "abuse-reports",
          route: "/reports/abuse-reports",
        },
        {
          title: "private-error-reports",
          route: "/reports/private-error-reports",
        },
        {
          title: "private-abuse-reports",
          route: "/reports/private-abuse-reports",
        },
        {
          title: "claims",
          route: "/reports/claims",
        },
      ],
    },
    {
      id: "evaluations",
      title: "evaluations",
      route: "/evaluations",
      icon: "reviews",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
    {
      id: "pages",
      title: "pages",
      route: "/pages/about-app",
      icon: "support",
      hasChildren: true,
      group: "governance" as MenuGroup,
      children: [
        {
          title: "about-app",
          route: "/pages/about-app",
        },
        {
          title: "terms-conditions",
          route: "/pages/terms-conditions",
        },
        {
          title: "privacy-policy",
          route: "/pages/privacy-policy",
        },
        {
          title: "faqs",
          route: "/pages/faqs",
        },
      ],
    },
    {
      id: "update-version",
      title: "update-version",
      route: "/update-version",
      icon: "barcode",
      hasChildren: false,
      group: "governance" as MenuGroup,
    },
    // 2026-08-07 — the tenant-admin console had routes, guards and finished
    // components (/organization-settings, /mobile-page-config) but no menu
    // entry, so branding + mobile page config were reachable only by typing
    // the URL. Gated by TENANT_ADMIN_ONLY_ITEMS to match the route's
    // TenantAdminGuard.
    {
      id: "institute-settings",
      title: "institute-settings",
      route: "/organization-settings",
      icon: "domain",
      hasChildren: true,
      group: "governance" as MenuGroup,
      children: [
        {
          title: "organization-settings",
          route: "/organization-settings",
        },
        {
          title: "mobile-page-config",
          route: "/mobile-page-config",
        },
      ],
    },
    {
      id: "super-admin-section",
      title: "super-admin",
      route: "/super/dashboard",
      icon: "admin_panel_settings",
      hasChildren: true,
      group: "platform" as MenuGroup,
      children: [
        {
          title: "super-dashboard",
          route: "/super/dashboard",
        },
        {
          title: "super-institutes",
          route: "/super/institutes",
        },
        {
          title: "super-users",
          route: "/super/users",
        },
        // 2026-05-06 Phase I — children below are filtered out of the
        // visible menu via BACKEND_GAP_ROUTE_SEGMENTS until backend
        // ships endpoints. UI is reachable by typing the URL today.
        {
          title: "super-onboarding",
          route: "/super/onboarding",
        },
        {
          title: "super-audit-log",
          route: "/super/audit-log",
        },
        {
          title: "super-feature-flags",
          route: "/super/feature-flags",
        },
      ],
    },
    {
      id: "feature-flags",
      title: "feature-flags",
      route: "/feature-flags",
      icon: "toggle_on",
      hasChildren: false,
      group: "governance" as MenuGroup,
    },
    {
      id: "analytics",
      title: "analytics",
      route: "/analytics",
      icon: "bar_chart",
      hasChildren: false,
      group: "operations" as MenuGroup,
    },
  ];
  @Output() sideNavToggleBtnRes = new EventEmitter<boolean>();
  secondaryColor = "var(--primary-color)";
  activeChildRoute!: string;
  isFreePlatform = false;

  get visibleMenu() {
    let menu = this.sideMenu;
    if (this.isFreePlatform) {
      menu = menu.filter((item) => !["payments", "money-transfer-requests"].includes(item.id));
    }

    // Hide institute-only items when a Maqra'a tenant_admin is active.
    // Super-admins always see everything so they can impersonate.
    if (this._tenantContext.isMaqraa() && this._tenantContext.isTenantAdmin()) {
      menu = menu.filter((item) => !INSTITUTE_ONLY_ITEMS.has(item.id));
    }

    // Tenant-pipeline is a super-admin-only control surface
    // (only super-admins approve new institute/maqra'a signups).
    if (!this._tenantContext.isSuperAdmin()) {
      menu = menu.filter((item) => !SUPER_ADMIN_ONLY_ITEMS.has(item.id));
    }

    // 2026-05-06 G+6 — Hide tenant-admin-only items (feature-flags,
    // CMS, update-version) from tenant_manager / supervisor.
    // super_admin always sees everything for impersonation.
    if (!this._tenantContext.isTenantAdmin() && !this._tenantContext.isSuperAdmin()) {
      menu = menu.filter((item) => !TENANT_ADMIN_ONLY_ITEMS.has(item.id));
    }

    // 2026-05-06 H-7 — analytics backend gap; hide everywhere.
    menu = menu.filter((item) => !BACKEND_GAP_ITEMS.has(item.id));

    // 2026-05-06 #1 (Phase I) — also filter children whose route
    // ends with a backend-gap segment (super/audit-log, super/
    // feature-flags, super/onboarding). Same defer pattern as
    // BACKEND_GAP_ITEMS but for sub-menu links.
    menu = menu.map((item) => {
      if (!item.children || item.children.length === 0) return item;
      const filteredChildren = item.children.filter(
        (child: MenuChild) => !BACKEND_GAP_ROUTE_SEGMENTS.some((seg) => child.route.endsWith("/" + seg))
      );
      return { ...item, children: filteredChildren };
    });

    return menu;
  }

  /**
   * 2026-05-07 — IA grouping. Buckets visibleMenu into ordered
   * sections (Operations / Governance / Platform). Empty sections
   * are dropped so we never render a stray header without items.
   */
  get visibleMenuSections(): MenuSection[] {
    const sourceItems = this.visibleMenu as MenuItem[];
    return MENU_GROUP_ORDER.map(({ group, titleKey }) => ({
      group,
      titleKey,
      items: sourceItems.filter((item) => (item.group ?? "operations") === group),
    })).filter((section) => section.items.length > 0);
  }

  constructor(
    private _router: Router,
    location: Location,
    private _dialog: MatDialog,
    private _user: UserService,
    private _snackBar: MatSnackBar,
    private _firebase: FirebaseService,
    public _platForm: PlatformService,
    public _translate: TranslateService,
    private _countriesService: CountriesService,
    private _tenantContext: TenantContextService
  ) {
    this._router.events.subscribe((val) => {
      if (location.path() != "") {
        this.route = location.path();
      }
    });

    this._countriesService.appConfig$.subscribe((config: AppConfig | null) => {
      if (config) {
        this.isFreePlatform = !!config.isFreePlatform;
      }
    });

    this._countriesService.getConfigration().subscribe((raw) => {
      const res = raw as ConfigurationResponse;
      if (res?.data) {
        this._countriesService.appConfigSubj.next(res.data);
        // Phase G: seed deployment mode so super-admin gates apply.
        this._tenantContext.setDeploymentMode(res.data.deploymentMode);
      }
    });
  }

  isItemActive(item: MenuItem): boolean {
    if (!this.route) return false;
    if (item.hasChildren && item.children) {
      return item.children.some((child) => this.route.startsWith(child.route));
    }
    return this.route.startsWith(item.route);
  }

  isChildActive(child: MenuChild): boolean {
    if (!this.route) return false;
    return this.route.startsWith(child.route);
  }

  //toggle function to open and close sub menu in side nav bar
  toggle(value: boolean, openFlag: boolean, id?: string) {
    if (value && this.routeTitle != id) {
      value = true;
      if (id) this.routeTitle = id;
    } else {
      value = false;
      this.routeTitle = "";
    }
    this.openFlag = value;
    this.subMenu = value;
    this.toggleSubMenu.emit(value);
  }

  // go to pages
  goTo(route: string) {
    // check when side nav is close close sub menu when route
    if (this.sideNavToggleBtn) {
      this.toggle(false, false);
    }
    this._router.navigateByUrl(route);
    this.sideNavToggleBtnRes.emit(false);
  }

  // logout
  async logout() {
    const isLogout: boolean = await firstValueFrom(
      this._dialog
        .open(ConfirmDialogComponent, {
          width: "26.25rem",
          data: {
            title: this._translate.instant("login.logout"),
            content: this._translate.instant("login.are-you-want-logout"),
            action: { accept: "yes", refuse: "cancel", color: this.secondaryColor },
          },
        })
        .afterClosed()
    );

    if (isLogout) {
      this._snackBar
        .open(this._translate.instant("confirm-dialog.logout-message"), "", {
          panelClass: "success-snackbar",
          duration: 1500,
        })
        .afterDismissed()
        .subscribe(() => {
          this._firebase.logout().subscribe();
          this._user.logout();
          this._router.navigate(["/login"]);
          this._dialog.closeAll();
        });
    }
  }
}
